import { Hit } from 'algoliasearch/lite';
import {
  AppointmentDepositPlacement,
  Appointments,
  AppointmentsSchedulingType,
  Customers,
  DepositPaymentStatus,
  GeneralRequests,
  ItemCategory,
  PaymentMethod,
  ProviderCommissionType,
  RecipientUserRelation,
  Users,
  VirtualMeetingProvider,
} from '@prisma/client';
import * as yup from 'yup';
import type { AdditionalAppointment } from '../../utils/form/appointmentForm';
import { SORTABLE_KEYS } from '../../constants/appointments';
import { MAX_OFFERED_SLOTS } from '../../utils/form/offeredSlots';
import { QueueAppointment } from '../queue';
import type { Appointment, Service } from '../../interfaces';
import type {
  AdditionalServiceCheckoutItem,
  CheckoutCustomer,
  GiftCardType,
} from '../payments';

const ALGOLIA_FIELDS = [
  'tattooLocation',
  'tattooDetails',
  'tattooSize',
  'tattooColor',
  'isFirstTattoo',
  'isNewCustomer',
  'budget',
  'priceType',
  'price',
  'minPrice',
  'maxPrice',
  'hourlyPrice',
  'depositPrice',
  'rescheduledByCust',
  'rescheduledByEmployee',
  'refImgsId',
  'createdAt',
  'updatedAt',
  'startDateTime',
  'endDateTime',
  'tripCity',
  'timezone',
  'liabilityWaivedOn',
  'businessId',
  'checkoutSessionCompleted',
  'checkoutSessionActive',
  'tipAmt',
  'appointmentType',
  'serviceCategory',
  'serviceId',
  'status',
  'isForSomeoneElse',
  'recipientFirstName',
  'recipientLastName',
  'recipientDob',
  'recipientUserRelation',
  'customerGuestId',
] as const satisfies readonly (keyof Appointments)[];

export type CheckoutPaymentMethod =
  | 'CREDIT'
  | 'TERMINAL'
  | 'MANUAL'
  | 'CASH'
  | 'DIGITAL'
  | 'GIFT_CARD'
  | 'FREE';

export interface CustomCheckoutItem {
  itemId?: string | null;
  id: string;
  name: string;
  price: number;
  quantity: number;
  category?: ItemCategory;
}

export interface CheckoutFormValues {
  customer: {
    customerId: string;
    customerGuestId?: string;
    firstName: string;
    lastName: string | undefined;
    email: string | undefined;
    phoneNumber: string;
    dob?: string;
  };
  employeeId: string;
  appointmentId: string;
  startDateTime: string;
  price: number;
  tipAmt: number;
  /** Set instead of tipAmt when the tip was picked as a percentage. */
  tipPercentage?: number | null;
  isCashTaxed: boolean;
  paymentMethod: CheckoutPaymentMethod;
  customOrderItems?: CustomCheckoutItem[];
  additionalServices?: AdditionalServiceCheckoutItem[];
  providerCommissionType?: ProviderCommissionType;
  failedOrderId?: string;
  paymentIntentId?: string;
  discountId?: string;
  serviceId?: number;
  businessTripId?: string;
  giftCardType: GiftCardType;
}

export interface MarkDepositPaidPayload {
  depositPaymentMethod: PaymentMethod;
  depositPrice?: number;
}

export type UnarchivedAppointmentFields = Pick<
  Appointments,
  'status' | 'archivedAt'
>;
export type UnarchivedGeneralRequestFields = Pick<GeneralRequests, 'deletedAt'>;
export type UnarchiveAppointmentResult =
  | UnarchivedAppointmentFields
  | UnarchivedGeneralRequestFields;

export type SortableKeys = (typeof SORTABLE_KEYS)[number];

export const tipSchema = yup.object({
  tipAmt: yup
    .number()
    .min(0, 'Tip amount must be greater than or equal to 0')
    .nullable()
    .optional(),
});

export type TipState = yup.InferType<typeof tipSchema>;

export type AppointmentHit = Hit<
  {
    firstName: Users['firstName'];
    lastName: Users['lastName'];
    phoneNumber: Users['phoneNumber'];
    dob?: Customers['dob'];
    fullName: string;
    recordUpdatedAt: number;
    customerId: string;
  } & Pick<Appointments, (typeof ALGOLIA_FIELDS)[number]>
>;

export type SchedulingMode = 'BOOKING_LINK' | 'PICK_TIME' | 'OFFER_TIMES';
export type RequestKind = 'NEW_APPOINTMENT' | 'CONSULTATION';

export interface OfferedSlot {
  id: string;
  startDateTime: string;
}

export interface AcceptRequestFlowValues {
  requestKind: RequestKind;
  schedulingMode: SchedulingMode;
  businessId?: string;
  employeeId: string;
  appointmentLengthInMinutes: string;
  priceType: string;
  price?: number;
  minPrice?: number;
  maxPrice?: number;
  hourlyPrice?: number;
  depositPrice?: number;
  startDateTime?: string;
  offeredSlots: string[];
  additionalAppointments: AdditionalAppointment[];
  isVirtual?: boolean;
  virtualMeetingUrl?: string;
  virtualMeetingProvider?: VirtualMeetingProvider | null;
  allowOverlapping?: boolean;
  numberOfSessions: number;
  schedulingType?: AppointmentsSchedulingType | '';
  bufferDays?: number | '';
  depositPlacement?: AppointmentDepositPlacement | '';
  customDesignMessage?: string;
  requestWaiver?: boolean;
  depositPaid?: boolean;
  depositPaymentMethod?: PaymentMethod;
  internalNotes?: string;
  cashDiscountEnabled?: boolean;
  serviceId?: string;
  businessTripId?: string;
  tattooLocation?: string;
  bodySide?: string;
  tattooSize?: string;
  tattooColor?: string;
  tattooDetails?: string;
  locationCategory?: string;
  piercingLocationId?: string;
}

const whenPricedAppointment = (
  requestKind: string,
  priceType: string,
  expectedPriceType: string,
) => requestKind !== 'CONSULTATION' && priceType === expectedPriceType;

export const acceptRequestFlowSchema = yup.object().shape({
  requestKind: yup
    .mixed<'NEW_APPOINTMENT' | 'CONSULTATION'>()
    .oneOf(['NEW_APPOINTMENT', 'CONSULTATION'])
    .required(),
  schedulingMode: yup
    .mixed<SchedulingMode>()
    .oneOf(['BOOKING_LINK', 'PICK_TIME', 'OFFER_TIMES'])
    .required(),
  businessId: yup.string().optional(),
  employeeId: yup.string().required('Artist is required'),
  appointmentLengthInMinutes: yup
    .number()
    .required('Please select an appointment length'),
  priceType: yup.string().when('requestKind', {
    is: 'NEW_APPOINTMENT',
    then: schema => schema.required('Please select a price type'),
    otherwise: schema => schema.optional(),
  }),
  price: yup
    .number()
    .min(0)
    .when(['requestKind', 'priceType'], {
      is: (requestKind: string, priceType: string) =>
        whenPricedAppointment(requestKind, priceType, 'FIXED'),
      then: schema => schema.required('Price is required'),
      otherwise: schema => schema.optional(),
    }),
  hourlyPrice: yup
    .number()
    .min(0)
    .when(['requestKind', 'priceType'], {
      is: (requestKind: string, priceType: string) =>
        whenPricedAppointment(requestKind, priceType, 'HOURLY'),
      then: schema => schema.required('Hourly rate is required'),
      otherwise: schema => schema.optional(),
    }),
  minPrice: yup
    .number()
    .min(0)
    .when(['requestKind', 'priceType'], {
      is: (requestKind: string, priceType: string) =>
        whenPricedAppointment(requestKind, priceType, 'RANGE'),
      then: schema => schema.required('Minimum price is required'),
      otherwise: schema => schema.optional(),
    }),
  maxPrice: yup
    .number()
    .min(0)
    .when(['requestKind', 'priceType'], {
      is: (requestKind: string, priceType: string) =>
        whenPricedAppointment(requestKind, priceType, 'RANGE'),
      then: schema =>
        schema.required('Maximum price is required').test(
          'max-not-less-than-min',
          'Max price cannot be less than min price',
          // eslint-disable-next-line func-names
          function (value) {
            const { minPrice } = this.parent;
            return (value ?? 0) >= (minPrice ?? 0);
          },
        ),
      otherwise: schema => schema.optional(),
    }),
  // Required so a $0 deposit is an explicit choice, never a silent default
  depositPrice: yup
    .number()
    .min(0)
    .when('requestKind', {
      is: 'NEW_APPOINTMENT',
      then: schema =>
        schema.required('Please enter a deposit (0 for no deposit)'),
      otherwise: schema => schema.optional(),
    }),
  startDateTime: yup.string().when('schedulingMode', {
    is: 'PICK_TIME',
    then: schema => schema.required('Please select a date & time'),
    otherwise: schema => schema.optional(),
  }),
  offeredSlots: yup.array().when('schedulingMode', {
    is: 'OFFER_TIMES',
    then: schema =>
      schema
        .of(yup.string().required())
        .min(1, 'Please select at least one time')
        .max(MAX_OFFERED_SLOTS, `Select up to ${MAX_OFFERED_SLOTS} times`),
    otherwise: schema => schema,
  }),
  // Multi-session rules are gated on requestKind as well as mode: switching
  // to a consultation hides the session fields but keeps their stale values,
  // and a required-but-unrendered field blocks submit with no visible error
  additionalAppointments: yup.array().when(['schedulingMode', 'requestKind'], {
    is: (schedulingMode: string, requestKind: string) =>
      schedulingMode === 'PICK_TIME' && requestKind !== 'CONSULTATION',
    then: schema =>
      schema.of(
        yup.object().shape({
          appointmentLengthInMinutes: yup
            .number()
            .transform(val => (Number.isNaN(val) ? undefined : val))
            .required('Please select a length'),
          startDateTime: yup.string().required('Please select a date & time'),
        }),
      ),
    otherwise: schema => schema,
  }),
  numberOfSessions: yup
    .number()
    .integer('Number of sessions must be an integer')
    .min(1, 'Number of sessions must be greater than 0')
    .required('Please select a number of sessions'),
  schedulingType: yup
    .mixed<AppointmentsSchedulingType>()
    .transform(val => val || undefined)
    .oneOf(
      Object.values(AppointmentsSchedulingType),
      'Please select a scheduling type',
    )
    .when(['numberOfSessions', 'schedulingMode', 'requestKind'], {
      is: (
        numberOfSessions: number,
        schedulingMode: string,
        requestKind: string,
      ) =>
        numberOfSessions > 1 &&
        schedulingMode === 'BOOKING_LINK' &&
        requestKind !== 'CONSULTATION',
      then: schema => schema.required('Please select a scheduling type'),
      otherwise: schema => schema.optional(),
    }),
  bufferDays: yup
    .number()
    .transform(val => (Number.isNaN(val) ? undefined : val))
    .integer('Buffer days must be an integer')
    .min(0)
    .optional(),
  depositPlacement: yup
    .mixed<AppointmentDepositPlacement>()
    .transform(val => val || undefined)
    .oneOf(
      Object.values(AppointmentDepositPlacement),
      'Please select a deposit placement',
    )
    .when(['numberOfSessions', 'depositPrice', 'requestKind'], {
      is: (
        numberOfSessions: number,
        depositPrice: number,
        requestKind: string,
      ) =>
        numberOfSessions > 1 &&
        (depositPrice ?? 0) > 0 &&
        requestKind !== 'CONSULTATION',
      then: schema => schema.required('Please select a deposit placement'),
      otherwise: schema => schema.optional(),
    }),
  customDesignMessage: yup.string().optional(),
  requestWaiver: yup.boolean().optional(),
  depositPaid: yup.boolean().optional(),
  depositPaymentMethod: yup.mixed<PaymentMethod>().nullable().optional(),
  internalNotes: yup.string().optional(),
  isVirtual: yup.boolean().optional(),
  allowOverlapping: yup.boolean().optional(),
  cashDiscountEnabled: yup.boolean().optional(),
});

export const durationFormSchema = yup.object().shape({
  appointmentLengthInMinutes: yup
    .number()
    .integer('Appointment length must be an integer')
    .min(1, 'Appointment length must be greater than 0')
    .required('Please select a duration'),
});

export type DurationFormState = yup.InferType<typeof durationFormSchema>;

export interface TakenSessionSlot {
  startDateTime: string;
  endDateTime: string;
}

export const appointmentRefundDepositSchema = yup.object({
  depositStatus: yup
    .mixed<DepositPaymentStatus>()
    .oneOf(Object.values(DepositPaymentStatus), 'Please select an option')
    .required('Please select an option'),
});

export type AppointmentRefundDepositState = yup.InferType<
  typeof appointmentRefundDepositSchema
>;

export const appointmentTransferDepositSchema = yup.object({
  fromAppointmentId: yup
    .string()
    .required('Please select an appointment to transfer from'),
  toAppointmentId: yup
    .string()
    .required('Please select an appointment to transfer to'),
});

export type AppointmentTransferDepositState = yup.InferType<
  typeof appointmentTransferDepositSchema
>;

export const finalTransferAppointmentDepositSchema = yup.object({
  fromAppointmentId: yup.string().required('Please select an appointment'),
  toAppointmentId: yup.string().required('Please select an appointment'),
  businessId: yup.string().required('Please refresh the page'),
  customerId: yup.string().required('Please refresh the page'),
});

export const finalTransferPaymentDepositSchema = yup.object({
  fromPaymentId: yup.string().required('Please select a payment'),
  toAppointmentId: yup.string().required('Please select an appointment'),
  businessId: yup.string().required('Please refresh the page'),
  customerId: yup.string().required('Please refresh the page'),
});

export type TransferAppointmentDepositPayload = yup.InferType<
  typeof finalTransferAppointmentDepositSchema
>;

export type TransferPaymentDepositPayload = yup.InferType<
  typeof finalTransferPaymentDepositSchema
>;

export interface AppointmentDepositCheckoutSession {
  isPaymentRequired: boolean;
  depositCents: number;
  serviceFeeCents: number;
  salesTaxCents: number;
  isDepositPaid: boolean;
  serviceFeeRate: number;
  businessTaxRate: number;
  totalPriceCents: number;
  feelessTotalPriceCents: number;
  cashDiscountEnabled: boolean;
  areOffPlatformDepositsTaxed: boolean;
  paymentIntent:
    | {
        id: string;
        client_secret: string;
        paymentId: string;
      }
    | undefined
    | null;
  paymentMethod: PaymentMethod;
  appointmentId: string;
}

export interface PaymentForDepositTransfer {
  paymentId: string;
  customer: Customers & {
    user: Users;
  };
  depositPrice: number;
  date: string;
}

export type SelectedAppointment = Appointment | QueueAppointment;

export type DepositCheckoutFormValues = {
  appointmentId: string;
  employeeId: string | undefined;
  depositPrice: number;
  paymentMethod: string;
  customer: CheckoutCustomer;
  providerCommissionType: ProviderCommissionType | '';
};

export interface CustomerDetails {
  firstName: string;
  lastName: string;
  dob: string | undefined;
  phoneNumber: string | undefined;
  email: string | undefined;
  primaryCustomer?: {
    firstName: string;
    lastName: string;
    dob: string | undefined;
    phoneNumber: string | undefined;
    email: string | undefined;
    relation: RecipientUserRelation;
    label: string;
  };
}

export interface ServiceWithHourlyPrice extends Service {
  defaultHourlyPrice?: number;
}
